import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Gauge, Pencil, Calendar, Building2, Fuel, Zap, Download, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge, DeadlineBadge } from "@/components/common/Badges";
import ContraventionsView from "@/components/common/ContraventionModule";
import { eur, km, fdate, fdatetime, monthsBetween, fmonths, renewalStatus, RENEWAL_META } from "@/lib/format";

const Info = ({ label, value }) => (
  <div><dt className="text-xs uppercase tracking-wide text-[#8695AB]">{label}</dt><dd className="mt-0.5 font-medium text-[#18221F]">{value || "—"}</dd></div>
);

const MiniTable = ({ cols, rows, empty }) => {
  if (!rows || rows.length === 0) return <p className="py-8 text-center text-sm text-[#8695AB]">{empty || "Aucune donnée"}</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#E2E8F0] bg-[#F5F7FA] text-left text-xs uppercase tracking-wide text-[#4A5568]">{cols.map((c) => <th key={c.label} className="px-3 py-2 font-semibold">{c.label}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-[#EDF0F5] last:border-0">{cols.map((c) => <td key={c.label} className="px-3 py-2 text-[#18221F]">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
};

export default function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [v, setV] = useState(null);
  const [mileageOpen, setMileageOpen] = useState(false);
  const [newMileage, setNewMileage] = useState("");
  const [fuel, setFuel] = useState(null);
  const [docViewer, setDocViewer] = useState(null);

  useEffect(() => { api.get(`/fuel/vehicles/${id}/stats`).then((r) => setFuel(r.data)).catch(() => {}); }, [id]);

  const load = () => api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveMileage = async () => {
    try {
      await api.put(`/vehicles/${id}`, { mileage: Number(newMileage) });
      toast.success("Kilométrage mis à jour");
      setMileageOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDoc = async (r) => {
    try {
      const res = await api.get(`/documents/${r.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: r.content_type || res.data.type || "application/pdf" });
      setDocViewer({ id: r.id, name: r.original_filename, url: URL.createObjectURL(blob) });
    } catch (e) { toast.error(apiError(e)); }
  };
  const closeDoc = () => setDocViewer((d) => { if (d?.url) URL.revokeObjectURL(d.url); return null; });
  const downloadDoc = async (r) => {
    try {
      const res = await api.get(`/documents/${r.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = r.name || r.original_filename || "document"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!v) return <div className="space-y-4"><Skeleton className="h-10 w-40" /><Skeleton className="h-72 w-full rounded-2xl" /></div>;

  const TABS = [
    ["informations", "Informations"], ["affectation", "Affectation"], ["contrat", "Contrat"],
    ["entretien", "Entretien"], ["assurance", "Assurance"], ["ct", "Contrôle technique"],
    ["pneus", "Pneumatiques"], ["sinistres", "Sinistres"], ["carburant", "Carburant"], ["contraventions", "Contraventions"], ["depenses", "Dépenses"],
    ["documents", "Documents"], ["historique", "Historique"],
  ];

  return (
    <div>
      <button onClick={() => nav("/vehicles")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#001A3B] hover:underline" data-testid="back-to-vehicles"><ArrowLeft className="h-4 w-4" />Retour aux véhicules</button>

      {v.status === "restitue" && (
        <div className="mb-4 flex items-start gap-3 rounded-2xl border border-gray-300 bg-gray-100 px-5 py-4 text-sm text-gray-800" data-testid="vehicle-returned-banner">
          <Lock className="mt-0.5 h-5 w-5 shrink-0 text-gray-600" />
          <div><p className="font-semibold">Véhicule restitué{v.restitution_date ? ` le ${fdate(v.restitution_date)}` : ""}</p>
            <p className="text-gray-600">Fiche en lecture seule : le véhicule n'est plus affectable et n'apparaît plus dans les indicateurs actifs. Son historique et ses dépenses sont conservés.</p></div>
        </div>
      )}

      {/* Hero */}
      <div className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white">
        <div className="relative h-56 bg-[#EEF2F8] sm:h-72">
          {v.photo && <img src={v.photo} alt={v.model} className="h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
            <div>
              <div className="mb-2 flex items-center gap-2"><StatusBadge status={v.status} />{v.archived && <span className="rounded-full border border-amber-300/70 bg-amber-100/90 px-2.5 py-0.5 text-xs font-semibold text-amber-900" data-testid="vehicle-archived-badge">Archivé{v.archived_at ? ` le ${fdate(v.archived_at)}` : ""}</span>}</div>
              <h1 className="font-heading text-3xl font-bold">{v.brand} {v.model}</h1>
              <p className="text-sm text-white/80">{v.version} · {v.year} · {v.energy}</p>
            </div>
            <span className="rounded-lg border border-white/30 bg-black/30 px-3 py-1.5 font-mono text-sm backdrop-blur">{v.registration}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-4">
          <div className="flex items-center gap-2"><Gauge className="h-5 w-5 text-[#001A3B]" /><div><div className="text-xs text-[#8695AB]">Kilométrage</div><div className="font-semibold text-[#18221F]">{km(v.mileage)}</div></div></div>
          <div className="flex items-center gap-2">{v.energy === "Electrique" ? <Zap className="h-5 w-5 text-[#001A3B]" /> : <Fuel className="h-5 w-5 text-[#001A3B]" />}<div><div className="text-xs text-[#8695AB]">Conducteur</div><div className="font-semibold text-[#18221F]">{v.current_driver || "Non affecté"}</div></div></div>
          <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-[#001A3B]" /><div><div className="text-xs text-[#8695AB]">Société</div><div className="font-semibold text-[#18221F]">{v.company}</div></div></div>
          <div className="flex items-center gap-2"><Calendar className="h-5 w-5 text-[#001A3B]" /><div><div className="text-xs text-[#8695AB]">Prochaine échéance</div>{v.next_deadline_date ? <DeadlineBadge level={v.next_deadline_status}>{fdate(v.next_deadline_date)}</DeadlineBadge> : <span className="text-sm text-[#8695AB]">—</span>}</div></div>
        </div>
        {v.status !== "restitue" && <div className="flex gap-2 border-t border-[#E2E8F0] p-4">
          <Button onClick={() => { setNewMileage(String(v.mileage)); setMileageOpen(true); }} variant="outline" data-testid="update-mileage-btn"><Gauge className="mr-2 h-4 w-4" />Mettre à jour le kilométrage</Button>
        </div>}
      </div>

      {/* Tabs */}
      <div className="mt-6">
        <Tabs defaultValue="informations">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
            {TABS.map(([val, label]) => (
              <TabsTrigger key={val} value={val} data-testid={`vehicle-tab-${val}`} className="rounded-lg border border-[#E2E8F0] bg-white data-[state=active]:bg-[#001A3B] data-[state=active]:text-white">{label}</TabsTrigger>
            ))}
          </TabsList>

          <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white p-6">
            <TabsContent value="informations" className="mt-0">
              <dl className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                <Info label="Immatriculation" value={v.registration} /><Info label="Marque" value={v.brand} />
                <Info label="Modèle" value={v.model} /><Info label="Version" value={v.version} />
                <Info label="VIN" value={v.vin} /><Info label="1ère mise en circ." value={fdate(v.first_registration)} />
                <Info label="Année" value={v.year} /><Info label="Couleur" value={v.color} />
                <Info label="Énergie" value={v.energy} /><Info label="Puissance" value={v.power ? `${v.power} ch` : "—"} />
                <Info label="Boîte de vitesse" value={v.gearbox} /><Info label="Type" value={v.type} />
                <Info label="Kilométrage" value={km(v.mileage)} /><Info label="Statut" value={<StatusBadge status={v.status} />} />
                <Info label="Loueur" value={v.loueur} />
                <Info label="Date d'achat" value={v.purchase_date ? fdate(v.purchase_date) : "—"} />
                <Info label="Âge du véhicule" value={v.purchase_date ? fmonths(monthsBetween(v.purchase_date)) : "—"} />
                <Info label="Renouvellement" value={renewalStatus(v) ? <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${RENEWAL_META[renewalStatus(v)].cls}`} data-testid="vehicle-renewal-status">{RENEWAL_META[renewalStatus(v)].label}</span> : "—"} />
                <Info label="Prolongation +1 an" value={v.extension_granted ? "Accordée" : "Non"} />
                {v.restitution_date && <Info label="Date de restitution" value={fdate(v.restitution_date)} />}
              </dl>
            </TabsContent>

            <TabsContent value="affectation" className="mt-0">
              {v.current_employee && (
                <div className="mb-4 rounded-lg bg-[#EEF2F8] p-4">
                  <div className="text-xs uppercase text-[#8695AB]">Conducteur actuel</div>
                  <div className="font-heading text-lg font-semibold text-[#001A3B]">{v.current_employee.first_name} {v.current_employee.last_name}</div>
                  <div className="text-sm text-[#4A5568]">{v.current_employee.service} · {v.current_employee.fonction} · {v.current_employee.site}</div>
                </div>
              )}
              <MiniTable empty="Aucun historique d'affectation" rows={v.assignments} cols={[
                { label: "Début", render: (r) => fdate(r.start_date) }, { label: "Fin", render: (r) => r.end_date ? fdate(r.end_date) : "En cours" },
                { label: "Site", key: "site" }, { label: "Service", key: "service" }, { label: "Fonction", key: "fonction" },
                { label: "Statut", render: (r) => r.active ? <span className="text-emerald-700">Active</span> : "Clôturée" },
              ]} />
            </TabsContent>

            <TabsContent value="contrat" className="mt-0">
              <MiniTable empty="Aucun contrat" rows={v.contracts} cols={[
                { label: "Type", key: "type" }, { label: "Fournisseur", key: "supplier" }, { label: "N°", key: "contract_number" },
                { label: "Début", render: (r) => fdate(r.start_date) }, { label: "Fin", render: (r) => fdate(r.end_date) },
                { label: "Loyer", render: (r) => eur(r.monthly_rent) }, { label: "Km contract.", render: (r) => km(r.contractual_mileage) },
              ]} />
            </TabsContent>

            <TabsContent value="entretien" className="mt-0">
              <MiniTable empty="Aucun entretien" rows={v.maintenance} cols={[
                { label: "Date", render: (r) => fdate(r.date) }, { label: "Type", key: "type" }, { label: "Garage", key: "garage" },
                { label: "Km", render: (r) => km(r.mileage) }, { label: "TTC", render: (r) => eur(r.cost_ttc) }, { label: "Prochaine", render: (r) => fdate(r.next_date) },
              ]} />
            </TabsContent>

            <TabsContent value="assurance" className="mt-0">
              <MiniTable empty="Aucune assurance" rows={v.insurances} cols={[
                { label: "Assureur", key: "insurer" }, { label: "N°", key: "contract_number" }, { label: "Début", render: (r) => fdate(r.start_date) },
                { label: "Fin", render: (r) => fdate(r.end_date) }, { label: "Prime", render: (r) => eur(r.premium) }, { label: "Franchise", render: (r) => eur(r.franchise) },
              ]} />
              {(v.documents || []).filter((d) => (d.category || "").toLowerCase() === "assurance").length > 0 && (
                <div className="mt-5">
                  <p className="mb-2 font-heading text-sm font-semibold text-[#001A3B]">Documents d'assurance</p>
                  <MiniTable empty="Aucun document d'assurance" rows={(v.documents || []).filter((d) => (d.category || "").toLowerCase() === "assurance")} cols={[
                    { label: "Fichier", key: "original_filename" }, { label: "Ajouté le", render: (r) => fdate(r.created_at) },
                    { label: "", render: (r) => <button onClick={() => openDoc(r)} className="text-[#001A3B] underline" data-testid="ins-doc-open">Ouvrir</button> },
                  ]} />
                </div>
              )}
            </TabsContent>

            <TabsContent value="ct" className="mt-0">
              <MiniTable empty="Aucun contrôle technique" rows={v.technical_inspections} cols={[
                { label: "Date", render: (r) => fdate(r.date) }, { label: "Prochaine", render: (r) => fdate(r.next_date) },
                { label: "Résultat", render: (r) => <span className={r.result === "Favorable" ? "text-emerald-700" : "text-red-700"}>{r.result}</span> }, { label: "Centre", key: "center" },
              ]} />
            </TabsContent>

            <TabsContent value="pneus" className="mt-0">
              <MiniTable empty="Aucun pneumatique" rows={v.tyres} cols={[
                { label: "Type", key: "type" }, { label: "Marque", key: "brand" }, { label: "Dimension", key: "dimension" },
                { label: "Montage", render: (r) => fdate(r.mounting_date) }, { label: "État", key: "condition" }, { label: "Remplacement", render: (r) => fdate(r.next_replacement) },
              ]} />
            </TabsContent>

            <TabsContent value="sinistres" className="mt-0">
              <MiniTable empty="Aucun sinistre" rows={v.accidents} cols={[
                { label: "N°", key: "file_number" }, { label: "Date", render: (r) => fdate(r.date) }, { label: "Type", key: "type" },
                { label: "Responsabilité", key: "responsibility" }, { label: "Montant", render: (r) => eur(r.amount) }, { label: "Statut", key: "status" },
              ]} />
            </TabsContent>

            <TabsContent value="contraventions" className="mt-0">
              <ContraventionsView scope={{ vehicle_id: id }} showFilters={false} />
            </TabsContent>

            <TabsContent value="depenses" className="mt-0">
              <MiniTable empty="Aucune dépense" rows={v.expenses} cols={[
                { label: "Date", render: (r) => fdate(r.date) }, { label: "Catégorie", key: "category" }, { label: "Description", key: "description" }, { label: "Montant", render: (r) => eur(r.amount) },
              ]} />
            </TabsContent>

            <TabsContent value="carburant" className="mt-0">
              {!fuel || fuel.fills === 0 ? (
                <p className="py-8 text-center text-sm text-[#8695AB]">Aucune donnée carburant. Importez le fichier mensuel depuis Administration › Carburant.</p>
              ) : (
                <div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    {[["Litres", `${fuel.total_liters} L`], ["Montant", eur(fuel.total_amount)], ["Prix moyen", `${fuel.avg_price} €/L`], ["Conso. moy.", fuel.l100 ? `${fuel.l100} L/100` : "—"], ["Coût/km", fuel.cost_km ? `${fuel.cost_km} €` : "—"], ["Pleins", fuel.fills]].map(([l, v]) => (
                      <div key={l} className="rounded-xl border border-[#E2E8F0] bg-[#F5F7FA] p-3"><div className="text-[11px] uppercase text-[#8695AB]">{l}</div><div className="mt-1 font-heading text-lg font-bold text-[#18221F]">{v}</div></div>
                    ))}
                  </div>
                  {fuel.monthly.length > 0 && (
                    <div className="mt-5 h-56"><ResponsiveContainer width="100%" height="100%"><BarChart data={fuel.monthly}><CartesianGrid strokeDasharray="3 3" stroke="#EDF1F6" vertical={false} /><XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8695AB" }} axisLine={false} tickLine={false} /><YAxis tick={{ fontSize: 11, fill: "#8695AB" }} axisLine={false} tickLine={false} width={45} /><Tooltip formatter={(v, n) => n === "amount" ? eur(v) : `${v} L`} /><Bar dataKey="amount" name="Montant" fill="#001A3B" radius={[5, 5, 0, 0]} /><Bar dataKey="liters" name="Litres" fill="#A5894E" radius={[5, 5, 0, 0]} /></BarChart></ResponsiveContainer></div>
                  )}
                  <div className="mt-5"><MiniTable empty="Aucun plein" rows={fuel.transactions} cols={[
                    { label: "Date", render: (r) => fdate(r.date) }, { label: "Produit", key: "fuel_type" }, { label: "Station", key: "station" },
                    { label: "Litres", render: (r) => `${r.liters ?? "—"} L` }, { label: "PU", render: (r) => r.price_per_liter ? `${r.price_per_liter} €` : "—" },
                    { label: "Montant", render: (r) => eur(r.amount) }, { label: "Km", render: (r) => r.mileage ? km(r.mileage) : "—" },
                  ]} /></div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="documents" className="mt-0">
              <MiniTable empty="Aucun document — ajoutez-les depuis la page Documents" rows={v.documents} cols={[
                { label: "Fichier", key: "original_filename" }, { label: "Catégorie", key: "category" }, { label: "Ajouté le", render: (r) => fdate(r.created_at) },
                { label: "", render: (r) => <button onClick={() => openDoc(r)} className="text-[#001A3B] underline" data-testid="doc-open-btn">Ouvrir</button> },
              ]} />
            </TabsContent>

            <TabsContent value="historique" className="mt-0">
              <MiniTable empty="Aucun historique" rows={v.history} cols={[
                { label: "Date", render: (r) => fdatetime(r.created_at) }, { label: "Utilisateur", key: "user_name" }, { label: "Action", key: "description" },
                { label: "Ancienne valeur", key: "old_value" }, { label: "Nouvelle valeur", key: "new_value" },
              ]} />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      <Dialog open={mileageOpen} onOpenChange={setMileageOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Mettre à jour le kilométrage</DialogTitle></DialogHeader>
          <div className="py-2"><Label className="mb-1.5 block text-sm">Nouveau kilométrage</Label>
            <Input type="number" value={newMileage} onChange={(e) => setNewMileage(e.target.value)} data-testid="mileage-input" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setMileageOpen(false)}>Annuler</Button>
            <Button onClick={saveMileage} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="mileage-save-btn">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {docViewer && (
        <Dialog open onOpenChange={(o) => { if (!o) closeDoc(); }}>
          <DialogContent className="max-h-[95vh] w-[96vw] max-w-5xl">
            <DialogHeader><DialogTitle className="truncate pr-8 font-heading" data-testid="doc-viewer-title">{docViewer.name}</DialogTitle></DialogHeader>
            <iframe title={docViewer.name} src={docViewer.url} className="h-[76vh] w-full rounded-lg border border-[#E2E8F0] bg-[#F5F7FA]" data-testid="doc-viewer-frame" />
            <DialogFooter><Button variant="outline" onClick={() => downloadDoc(docViewer)} data-testid="doc-viewer-download"><Download className="mr-2 h-4 w-4" />Télécharger</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
